import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { FaExternalLinkAlt, FaEye } from "react-icons/fa";
import { LuMousePointer2 } from "react-icons/lu";
import Loader from "../../components/Loader";
import Calling from "../../api/Calling";

const ViewAds = () => {
  const [loader, setLoader] = useState(true);
  const [ad, setAd] = useState(null);
  const { id } = useParams();

  const fetchData = async () => {
    setLoader(true);
    try {
      const resp = await Calling.getAds("", "", "", id, "");
      if (resp?.status) {
        setAd(resp.data || null);
      }
    } catch (error) {
      console.error("Failed to fetch ad:", error);
      toast.error("Failed to load ad details");
    } finally {
      setLoader(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [id]);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  if (loader) {
    return <Loader loader={loader} />;
  }

  if (!ad) {
    return (
      <div className="flex justify-center items-center h-64">
        <p className="text-gray-500 text-lg">No ad found</p>
      </div>
    );
  }

  // Determine if the ad has a video
  const hasVideo =
    ad.type === "onlyvideo" || ad.type === "imagevideo" || ad.video;
  // Determine if the ad has an image
  const hasImage =
    ad.type === "onlyimage" || ad.type === "imagevideo" || ad.image;

  return (
    <div className="max-w-6xl mx-auto mt-20 p-4">
      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        {/* Header */}
        <div className="bg-gray-50 p-6 border-b">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
            <div className="flex items-center gap-4">
              {ad.icon && (
                <div className="w-16 h-16 rounded-lg overflow-hidden flex-shrink-0">
                  <img
                    src={ad.icon}
                    alt={`${ad.title} icon`}
                    className="w-full h-full object-cover"
                  />
                </div>
              )}
              <div>
                <h1 className="text-2xl font-bold text-gray-800">{ad.title}</h1>
                <p className="text-sm text-gray-500">
                  Created: {formatDate(ad.createdAt)}
                </p>
              </div>
            </div>
            <a
              href={ad.buttonUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-6 rounded-md flex items-center gap-2 transition-colors"
            >
              {ad.buttonLabel || "Visit"} <FaExternalLinkAlt size={16} />
            </a>
          </div>
        </div>

        {/* Ad Preview */}
        <div className="p-6">
          <h2 className="text-lg font-semibold text-gray-800 mb-4">
            Ad Preview
          </h2>
          <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
            <div className="aspect-video max-w-3xl mx-auto overflow-hidden rounded-lg">
              {hasVideo && ad.video ? (
                <video
                  src={ad.video}
                  controls
                  className="w-full h-full object-cover"
                  poster={ad.image} // Use image as poster if available
                >
                  Your browser does not support the video tag.
                </video>
              ) : hasImage && ad.image ? (
                <img
                  src={ad.image}
                  alt={ad.title}
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center bg-gray-200">
                  <p className="text-gray-500">No media available</p>
                </div>
              )}
            </div>

            {/* Media type and duration info */}
            <div className="mt-2 flex justify-between items-center text-sm text-gray-500">
              <span>Type: {ad.type || "unknown"}</span>
              {ad.display_duration && (
                <span>Duration: {ad.display_duration} seconds</span>
              )}
            </div>

            {ad.description && (
              <div className="mt-4 p-4 bg-white rounded border border-gray-200">
                <p className="text-gray-700">{ad.description}</p>
              </div>
            )}
          </div>
        </div>

        {/* Ad Stats */}
        <div className="p-6 bg-gray-50 border-t">
          <h2 className="text-lg font-semibold text-gray-800 mb-4">
            Performance Statistics
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-white p-4 rounded-lg border border-gray-200">
              <div className="flex items-center gap-3">
                <div className="bg-blue-100 p-2 rounded-full">
                  <FaEye className="text-blue-600" size={20} />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Total Views</p>
                  <p className="text-xl font-bold text-gray-800">
                    {ad.views || 0}
                  </p>
                </div>
              </div>
            </div>
            <div className="bg-white p-4 rounded-lg border border-gray-200">
              <div className="flex items-center gap-3">
                <div className="bg-green-100 p-2 rounded-full">
                  <LuMousePointer2 className="text-green-600" size={20} />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Total Clicks</p>
                  <p className="text-xl font-bold text-gray-800">
                    {ad.clicks || 0}
                  </p>
                </div>
              </div>
            </div>
          </div>
          {ad.notes && (
            <div className="mt-4 p-4 bg-white rounded-lg border border-gray-200">
              <h3 className="text-md font-semibold text-gray-700 mb-2">
                Notes
              </h3>
              <p className="text-gray-600">{ad.notes}</p>
            </div>
          )}
        </div>

        {/* Additional Info */}
        <div className="p-6 border-t">
          <h2 className="text-lg font-semibold text-gray-800 mb-4">
            Additional Information
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="p-4 bg-gray-50 rounded-lg">
              <p className="text-sm text-gray-500">Ad ID</p>
              <p className="text-gray-700 font-mono">{ad._id}</p>
            </div>
            <div className="p-4 bg-gray-50 rounded-lg">
              <p className="text-sm text-gray-500">Button Link</p>
              <a
                href={ad.buttonUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-600 hover:underline truncate block"
              >
                {ad.buttonUrl}
              </a>
            </div>
            {ad.type && (
              <div className="p-4 bg-gray-50 rounded-lg">
                <p className="text-sm text-gray-500">Ad Type</p>
                <p className="text-gray-700">{ad.type}</p>
              </div>
            )}
            {ad.display_duration && (
              <div className="p-4 bg-gray-50 rounded-lg">
                <p className="text-sm text-gray-500">Display Duration</p>
                <p className="text-gray-700">{ad.display_duration} seconds</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ViewAds;
